import {
  collectSseDataPayloadsFromResponse,
  parseJsonRpcFromSsePayloads,
} from './mcpSseJsonRpc';

interface JsonRpcRequest {
  jsonrpc: string;
  id: string | number;
  method: string;
  params?: unknown;
}

export interface McpExecutePayload {
  mcpServers: Record<string, JsonRpcRequest>;
}

// Module-level cache — populated on the first call to getLocalMcpServers()
let cachedServers: Record<string, string> | null = null;

/**
 * Parses REACT_APP_LOCAL_MCP_SERVERS once and caches the result.
 * Returns a `{ serverName: url }` map, or `{}` if the env var is unset or
 * contains invalid JSON.
 */
export function getLocalMcpServers(): Record<string, string> {
  if (cachedServers !== null) {
    return cachedServers;
  }

  const raw = process.env.REACT_APP_LOCAL_MCP_SERVERS;

  if (!raw) {
    cachedServers = {};
    return cachedServers;
  }

  try {
    cachedServers = JSON.parse(raw) as Record<string, string>;
  } catch (err) {
    console.warn(
      '[Bernoulli] Failed to parse REACT_APP_LOCAL_MCP_SERVERS',
      err,
    );
    cachedServers = {};
  }

  return cachedServers;
}

/**
 * Returns `true` if at least one local MCP server is configured.
 */
export function hasLocalMcpServers(): boolean {
  return Object.keys(getLocalMcpServers()).length > 0;
}

/**
 * Builds the mcpServers response in the format expected by UnifiedMcpProvider.
 */
export function handleMcpServers(): {
  mcpServers: Record<string, { url: string; title: string }>;
} {
  const servers = getLocalMcpServers();
  const mcpServers: Record<string, { url: string; title: string }> = {};

  for (const [name, url] of Object.entries(servers)) {
    mcpServers[name] = { url, title: name };
  }

  return { mcpServers };
}

/**
 * Proxies JSON-RPC requests to local MCP servers in parallel.
 * Handles both application/json and text/event-stream (SSE) responses
 * per the MCP Streamable HTTP transport spec.
 * Servers in the payload that are not in the local map are skipped.
 * On fetch failure, returns a JSON-RPC error response for that server.
 */
export async function handleMcpExecute(
  payload: McpExecutePayload,
  accessToken?: string,
): Promise<{ results: Record<string, unknown> }> {
  const servers = getLocalMcpServers();

  const entries = Object.entries(payload.mcpServers).filter(
    ([name]) => name in servers,
  );

  const settled = await Promise.all(
    entries.map(async ([name, request]) => {
      const url = servers[name];
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/event-stream',
        };
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
        });

        const contentType = response.headers.get('content-type') ?? '';

        if (contentType.includes('text/event-stream')) {
          const payloads = await collectSseDataPayloadsFromResponse(response);
          return [
            name,
            parseJsonRpcFromSsePayloads(payloads, request.id),
          ] as const;
        }

        return [name, await response.json()] as const;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return [
          name,
          {
            jsonrpc: '2.0',
            id: request.id,
            error: { code: -32000, message },
          },
        ] as const;
      }
    }),
  );

  const results: Record<string, unknown> = {};
  for (const [name, value] of settled) {
    results[name] = value;
  }

  return { results };
}
